import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Plus, Power, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, Fetcher, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  financeConfigCreate,
  financeConfigDelete,
  financeConfigList,
  financeConfigToggle,
  FIN_STR as S,
  type FinanceRow,
} from './financeApi'

/**
 * A10 config-CRUD jadvali (provider-settings / commission-rates / payment-limits /
 * test-mode / alerts). SeoConfigTable naqshi + `enabled` yoqish/o'chirish amali.
 * Backend `/admin/finance/config/:kind` (000241) — ro'yxat/upsert/toggle/o'chirish.
 */
export interface FinanceConfigField {
  name: string
  placeholder: string
  mono?: boolean
  options?: { value: string; label: string }[]
}

export interface FinanceConfigTableProps {
  screenId: string
  title: string
  description?: string
  addLabel: string
  /** URL slug = backend `kind` (masalan `provider-settings`). */
  kind: string
  searchKeys: string[]
  fields: FinanceConfigField[]
  columns: AdminColumn<FinanceRow>[]
  icon: ReactNode
}

export function FinanceConfigTable({
  screenId,
  title,
  description,
  addLabel,
  kind,
  searchKeys,
  fields,
  columns,
  icon,
}: FinanceConfigTableProps) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const firstName = fields[0]?.name ?? ''
  const canAdd = Boolean(form[firstName]?.trim())

  const add = async () => {
    if (!canAdd) return
    setBusy(true)
    try {
      const body: Record<string, string> = {}
      for (const f of fields) body[f.name] = (form[f.name] ?? '').trim()
      await financeConfigCreate(kind, body)
      toast(S.createdOk, { tone: 'success' })
      setForm({})
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<FinanceRow>[]>(
    () => [
      {
        id: 'toggle',
        label: S.enable + ' / ' + S.disable,
        icon: <Power size={14} />,
        onSelect: async (r) => {
          try {
            await financeConfigToggle(kind, r.id, !(r.enabled as boolean))
            toast(S.updatedOk, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
      {
        id: 'delete',
        label: S.delete,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await financeConfigDelete(kind, r.id)
            toast(S.deletedOk, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [kind, toast],
  )

  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<FinanceRow>>(
    () => financeConfigList<FinanceRow>(kind, searchKeyStr.split('|')),
    [kind, searchKeyStr],
  )

  return (
    <>
      <PageHeader title={title} screenId={screenId} description={description} />
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        {fields.map((f) =>
          f.options ? (
            <select
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            >
              <option value="">{f.placeholder}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              spellCheck={false}
              className={`min-w-[150px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong ${
                f.mono ? 'font-mono' : ''
              }`}
            />
          ),
        )}
        <button
          type="button"
          onClick={add}
          disabled={busy || !canAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {addLabel}
        </button>
      </div>
      <DataTable<FinanceRow>
        key={reloadKey}
        screenId={screenId}
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />
    </>
  )
}
